import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

import "./McpServerSettingsTab.scss";
import Message from "../../../i18n/Message";
import label from "../../../i18n/label";
import Notify from "../../shared/Notify";
import backend from "../../../backend/backend";
import ModalDialog from "../../shared/ModalDialog";
import PlaygroundAuthType from "../playgroundAuthType";
import { loadMcpPlaygrounds } from "../store/mcpPlaygroundActionCreators";
import { mcpPlaygroundSelectors } from "../store/mcpPlaygroundReducer";
import { useAutosave, AutosaveIndicator } from "../../shared/autosave";
import LiveChatManageEditor from "../playground-widget/live-chat/LiveChatManageEditor";
import useSubscriptionGate from "../../shared/subscription/useSubscriptionGate";
import UpgradeHint from "../../shared/subscription/UpgradeHint";
import { MAX_AUTH_TOKEN_LENGTH } from "../playgroundLimits";
import { oauthStartErrorMessageId } from "../oauth/oauthStartError";

/**
 * Settings tab for the playground details page. Consolidates what used to live in the
 * edit modal (name + URLs) and the widget's Live Chat editor (intro + prompts) into two
 * autosaving tiles. Every save merges its own fields onto the freshest playground snapshot
 * from the store, so the two tiles' independent autosaves can't overwrite each other.
 */
const McpServerSettingsTab = ({ playground }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { isReadOnly, can } = useSubscriptionGate();
  const liveChatAvailable = can("liveChat");
  const playgrounds = useSelector(mcpPlaygroundSelectors.getPlaygrounds);

  // Every edited field (from BOTH tiles) is accumulated here at edit time. A save always
  // overlays this onto the freshest store snapshot, so the two independent autosaves can't
  // revert each other's fields — even while a reload from a sibling save is still in flight
  // (the store snapshot lags the write, but pendingRef already holds the correct value).
  const pendingRef = useRef({});

  const latestBase = () =>
    playgrounds?.find((p) => p.uid === playground.uid) || playground;

  const persist = async () => {
    const saved = await backend.savePlayground({
      ...latestBase(),
      ...pendingRef.current,
    });
    // The server URL changed but the server's OAuth credentials could not be used to re-inspect it.
    // The edit itself was kept, so this is a notice rather than an error — without it the save looks
    // like it silently did nothing.
    if (saved?.savedWithoutReanalysis) {
      Notify.info(label(intl, "mcp_saved_reauth_needed"));
    }
    dispatch(loadMcpPlaygrounds(intl));
  };

  // ---- Tile 1: playground details (name + URLs) ----
  const [name, setName] = useState(playground.name || "");
  const [websiteUrl, setWebsiteUrl] = useState(playground.websiteUrl || "");
  const [mcpServerUrl, setMcpServerUrl] = useState(playground.mcpServerUrl || "");
  const nameRef = useRef(null);
  const websiteRef = useRef(null);
  const mcpRef = useRef(null);
  const [detailsInvalid, setDetailsInvalid] = useState(false);

  const detailsSave = useAutosave(async () => {
    try {
      await persist();
    } catch (e) {
      Notify.error(label(intl, "error_unexpected"));
      throw e;
    }
  });

  const allDetailsValid = () =>
    [nameRef, websiteRef, mcpRef].every(
      (r) => !r.current || r.current.checkValidity()
    );

  // Only VALID values are ever written to pendingRef, and the save reads pendingRef (not raw
  // state), so a still-armed debounce can never persist an invalid value. Reads live DOM
  // values off the refs so the pending payload reflects the just-typed value (state lags one
  // render). Name + website autosave on debounce — neither re-analyzes the MCP server.
  const onNameOrWebsiteChange = () => {
    setDetailsInvalid(!allDetailsValid());
    const n = nameRef.current;
    const w = websiteRef.current;
    if (![n, w].every((r) => !r || r.checkValidity())) return;
    pendingRef.current = {
      ...pendingRef.current,
      name: n ? n.value : name,
      websiteUrl: w ? w.value : websiteUrl,
    };
    detailsSave.schedule();
  };

  // Changing the MCP server URL re-introspects the server on the backend, so it must NOT
  // autosave on every keystroke (that would re-analyze partial URLs). It commits only when
  // the field is left (blur) or Enter is pressed, and only if it's a valid URL.
  const commitMcpUrl = () => {
    setDetailsInvalid(!allDetailsValid());
    const m = mcpRef.current;
    if (!m || !m.checkValidity()) return;
    if (m.value === (latestBase().mcpServerUrl || "")) return; // no change → no re-analysis
    pendingRef.current = { ...pendingRef.current, mcpServerUrl: m.value };
    detailsSave.schedule();
  };

  // Live Chat tile records its edits into the same pendingRef (at edit time) and saves
  // through the same `persist`, so it participates in the shared last-write-complete merge.
  // Its autosave status is lifted here so the indicator renders in the tile header, unified
  // with the Playground Details tile.
  const [liveChatStatus, setLiveChatStatus] = useState("idle");
  const onLiveChatChange = (fields) => {
    pendingRef.current = { ...pendingRef.current, ...fields };
  };
  const liveChatPersist = async (fields) => {
    pendingRef.current = { ...pendingRef.current, ...fields };
    await persist();
  };

  // ---- Tile 3: bearer token rotation (BEARER_TOKEN playgrounds only) ----
  // The stored token is @JsonIgnore and never returned, so this field always starts empty. Leaving
  // it empty means "keep the current token": the payload then carries no authToken at all and
  // McpPlaygroundController#update skips the overwrite, so an unrelated save can't wipe a working
  // token. Rotating a token deliberately does NOT re-analyze the server (only a URL change does).
  const isBearer = playground.authType === PlaygroundAuthType.BEARER_TOKEN;
  const [bearerToken, setBearerToken] = useState("");
  const bearerTokenRef = useRef(null);

  // Commits on blur / Enter like commitMcpUrl, never per keystroke: a debounced autosave would
  // persist a half-typed token and break the server's authentication mid-edit.
  const commitBearerToken = () => {
    const t = bearerTokenRef.current;
    const value = t ? t.value.trim() : "";
    if (!value) return; // blank -> leave the stored token untouched
    pendingRef.current = { ...pendingRef.current, authToken: value };
    detailsSave.schedule();
  };

  // ---- Tile 4: OAuth authentication (button-driven, NOT part of the autosave flow) ----
  const isOAuth = playground.authType === PlaygroundAuthType.OAUTH;
  const [oauthStatus, setOauthStatus] = useState(null);
  const [oauthBusy, setOauthBusy] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  const loadOauthStatus = () => {
    return backend
      .getMcpOAuthStatus(playground.uid)
      .then((status) => setOauthStatus(status))
      .catch((e) => console.log("Failed to load OAuth status", e));
  };

  useEffect(() => {
    if (!isOAuth) return;
    loadOauthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playground.uid, isOAuth]);

  // Starts the provider authorization and hands the browser over to it (used by Log in,
  // Log in as another account, and Reconnect — they are the same flow).
  const startOAuthLogin = () => {
    setOauthBusy(true);
    backend
      .startMcpOAuth(playground.uid)
      .then((res) => {
        window.location.href = res.authorizationUrl;
      })
      .catch(async (e) => {
        console.error("Failed to start OAuth", e);
        try {
          // Say which of the OAuth-specific failures it was; "something went wrong" gives the user
          // nothing to act on when the real answer is "this server can't register clients".
          Notify.error(
            label(intl, await oauthStartErrorMessageId(e, "error_unexpected"))
          );
        } finally {
          // In a finally so a throw while resolving the message cannot leave Log in / Reconnect
          // disabled with no way to retry.
          setOauthBusy(false);
        }
      });
  };

  const onOAuthLogout = () => {
    setOauthBusy(true);
    backend
      .mcpOAuthLogout(playground.uid)
      .then(() => loadOauthStatus())
      .then(() => {
        dispatch(loadMcpPlaygrounds(intl));
        setShowLogoutConfirm(false);
      })
      .catch((e) => {
        console.error("Failed to log out of OAuth", e);
        Notify.error(label(intl, "error_unexpected"));
      })
      .finally(() => setOauthBusy(false));
  };

  const identityLabel =
    oauthStatus?.identityEmail ||
    oauthStatus?.identityName ||
    label(intl, "settings_auth_account_fallback");

  return (
    <div className="settings_tab">
      {isReadOnly && (
        <div className="settings_readonly_banner">
          <Message id="trial_expired" />
          <UpgradeHint messageId="upgrade_hint_readonly" plan={undefined} />
        </div>
      )}
      <div className="settings_tile">
        <div className="tile_header">
          <div className="tile_title">
            <Message id="settings_playground_details_title" />
          </div>
          <AutosaveIndicator status={detailsSave.status} />
        </div>
        <div className="tile_body">
          <div className="settings_field">
            <label htmlFor="settings_playground_name">
              <Message id="create_playground_name" />
            </label>
            <input
              id="settings_playground_name"
              className="form-control"
              type="text"
              ref={nameRef}
              required
              disabled={isReadOnly}
              maxLength="120"
              placeholder={label(intl, "create_playground_name_placeholder")}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                onNameOrWebsiteChange();
              }}
            />
          </div>
          <div className="settings_field">
            <label htmlFor="settings_playground_website_url">
              <Message id="create_playground_website_url" />
            </label>
            <input
              id="settings_playground_website_url"
              className="form-control"
              type="url"
              ref={websiteRef}
              required
              disabled={isReadOnly}
              placeholder={label(
                intl,
                "create_playground_website_url_placeholder"
              )}
              value={websiteUrl}
              onChange={(e) => {
                setWebsiteUrl(e.target.value);
                onNameOrWebsiteChange();
              }}
            />
          </div>
          <div className="settings_field">
            <label htmlFor="settings_playground_mcp_server_url">
              <Message id="create_playground_mcp_url" />
            </label>
            <input
              id="settings_playground_mcp_server_url"
              className="form-control"
              type="url"
              ref={mcpRef}
              required
              disabled={isReadOnly}
              placeholder="e.g. https://your-mcp-server.com"
              value={mcpServerUrl}
              onChange={(e) => {
                setMcpServerUrl(e.target.value);
                setDetailsInvalid(!allDetailsValid());
              }}
              onBlur={commitMcpUrl}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  e.currentTarget.blur();
                }
              }}
            />
            <div className="settings_field_hint">
              <Message id="settings_mcp_url_reanalyze_hint" />
            </div>
          </div>
          {detailsInvalid && (
            <div className="settings_hint error">
              <Message id="settings_fix_fields_hint" />
            </div>
          )}
        </div>
      </div>

      <div className="settings_tile">
        <div className="tile_header">
          <div className="tile_title">
            <Message id="settings_live_chat_title" />
          </div>
          <AutosaveIndicator status={liveChatStatus} />
        </div>
        <div className="tile_body">
          {liveChatAvailable ? (
            <div className={isReadOnly ? "readonly_locked" : ""}>
              <LiveChatManageEditor
                playground={playground}
                persist={liveChatPersist}
                onChange={onLiveChatChange}
                onStatus={setLiveChatStatus}
                readOnly={isReadOnly}
              />
            </div>
          ) : (
            <UpgradeHint
              variant="blur"
              titleId="live_chat_locked_title"
              messageId="live_chat_locked_text"
              plan="pro"
            >
              <LiveChatManageEditor
                playground={playground}
                persist={liveChatPersist}
                onChange={onLiveChatChange}
                onStatus={setLiveChatStatus}
                readOnly={true}
              />
            </UpgradeHint>
          )}
        </div>
      </div>

      {isBearer && (
        <div className="settings_tile settings_auth_tile">
          <div className="tile_header">
            <div className="tile_title">
              <Message id="settings_auth_title" />
            </div>
            <AutosaveIndicator status={detailsSave.status} />
          </div>
          <div className="tile_body">
            <div className="settings_field">
              <label htmlFor="settings_playground_auth_token">
                <Message id="create_playground_auth_token_label" />
              </label>
              <input
                id="settings_playground_auth_token"
                className="form-control"
                type="text"
                ref={bearerTokenRef}
                disabled={isReadOnly}
                autoComplete="off"
                maxLength={MAX_AUTH_TOKEN_LENGTH}
                placeholder={label(
                  intl,
                  "create_playground_auth_token_placeholder"
                )}
                value={bearerToken}
                onChange={(e) => setBearerToken(e.target.value)}
                onBlur={commitBearerToken}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    e.currentTarget.blur();
                  }
                }}
              />
              <div className="settings_field_hint">
                <Message id="settings_auth_token_hint" />
              </div>
            </div>
          </div>
        </div>
      )}

      {isOAuth && (
        <div className="settings_tile settings_auth_tile">
          <div className="tile_header">
            <div className="tile_title">
              <Message id="settings_auth_title" />
            </div>
          </div>
          <div className="tile_body">
            {oauthStatus?.needsReauth && (
              <div className="settings_readonly_banner">
                <Message id="mcp_oauth_reauth_needed" />
                <button
                  className="btn btn-outline-dark btn-sm"
                  disabled={oauthBusy}
                  onClick={startOAuthLogin}
                >
                  <Message id="settings_auth_reconnect" />
                </button>
              </div>
            )}
            {oauthStatus?.connected ? (
              <>
                <div className="settings_auth_identity">
                  <Message
                    id="settings_auth_connected_as"
                    values={{ account: identityLabel }}
                  />
                </div>
                {oauthStatus.connectedAt && (
                  <div className="settings_field_hint">
                    <Message id="settings_auth_connected_at" />{" "}
                    {new Date(oauthStatus.connectedAt).toLocaleString()}
                  </div>
                )}
                {oauthStatus.scopes && (
                  <div className="settings_field_hint">
                    <Message id="settings_auth_scopes" /> {oauthStatus.scopes}
                  </div>
                )}
                <div className="settings_auth_actions">
                  <button
                    className="btn btn-outline-dark btn-sm"
                    disabled={oauthBusy}
                    onClick={() => setShowLogoutConfirm(true)}
                  >
                    <Message id="settings_auth_logout" />
                  </button>
                  <button
                    className="btn no-btn btn-sm"
                    disabled={oauthBusy}
                    onClick={startOAuthLogin}
                  >
                    <Message id="settings_auth_login_another" />
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="settings_auth_identity">
                  <Message id="settings_auth_not_connected" />
                </div>
                <div className="settings_auth_actions">
                  <button
                    className="btn bg-gradient-dark btn-sm"
                    disabled={oauthBusy}
                    onClick={startOAuthLogin}
                  >
                    <Message id="settings_auth_login" />
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {showLogoutConfirm && (
        <ModalDialog
          onClose={() => (oauthBusy ? null : setShowLogoutConfirm(false))}
          className="create_campaign_dialog"
        >
          <div className="modal_title">
            <Message id="settings_auth_logout" />
          </div>
          <div className="dialog_body">
            <Message id="settings_auth_logout_confirm" />
          </div>
          <div className="buttons">
            <button
              className="btn bg-gradient-primary btn-sm"
              disabled={oauthBusy}
              onClick={onOAuthLogout}
            >
              <Message id="settings_auth_logout" />
            </button>
            <button
              className="btn no-btn"
              disabled={oauthBusy}
              onClick={() => setShowLogoutConfirm(false)}
            >
              <Message id="cancel" />
            </button>
          </div>
        </ModalDialog>
      )}
    </div>
  );
};

export default McpServerSettingsTab;
